import { RichSelect } from 'common-front/src/designSystem/form/richSelect';
import { useHeavent } from 'common-front/src/hooks/useHeavent';
import { IntervalService } from 'common/src/services/intervalService';
import { useService } from 'common/src/util/dependencies/dependencies';
import { fullName } from 'common/src/vo/positionSlot';
import { sortBy } from 'lodash-es';
import * as React from 'react';
import { useWorkflowPositionsQuery } from '../../../generated/graphqlHooks';
import { WorkflowBox } from '../workflowBox';

interface IWorkflowPositionsStepProps {
    isEdit: boolean;
}

export const WorkflowPositionsStep = (props: IWorkflowPositionsStepProps) => {
    const {
        translate,
        params: { eventId }
    } = useHeavent();
    const intervalService = useService(IntervalService);
    const { data } = useWorkflowPositionsQuery({ eventId });
    const categoriesOptions = React.useMemo(
        () =>
            sortBy(data.event?.positionsCategories ?? [], (c) => c.name.toLowerCase()).map(
                (category) => (
                    <option key={category.id} value={category.id}>
                        {category.name}
                    </option>
                )
            ),
        [data.event]
    );
    const positionsOptions = React.useMemo(
        () =>
            sortBy(data.event?.positionsCategories ?? [], (c) => c.name.toLowerCase()).map(
                (category) => (
                    <optgroup key={category.id} label={category.name}>
                        {sortBy(category.positions, (p) => p.name.toLowerCase()).map((position) => (
                            <option key={position.id} value={position.id}>
                                {position.name}
                            </option>
                        ))}
                    </optgroup>
                )
            ),
        [data.event]
    );
    const slotsOptions = React.useMemo(
        () =>
            (data.event?.positionsCategories ?? []).flatMap((category) =>
                sortBy(category.positions, (p) => p.name.toLowerCase()).map((position) => (
                    <optgroup key={position.id} label={position.name}>
                        {sortBy(position.slots, (s) => s.range.start!.toMillis()).map((slot) => {
                            const name = fullName(intervalService, slot, position.name, {
                                position: { includeNameAtEnd: true },
                                slot: { includeRange: true }
                            });

                            return (
                                <option key={slot.id} value={slot.id}>
                                    {name}
                                </option>
                            );
                        })}
                    </optgroup>
                ))
            ),
        [data.event]
    );
    const segmentsOptions = React.useMemo(
        () =>
            (data.event?.segmentsFolders ?? []).map((segmentFolder) => (
                <optgroup key={segmentFolder.id} label={segmentFolder.name}>
                    {segmentFolder.segments.map((segment) => (
                        <option key={segment.id} value={segment.id}>
                            {segment.name}
                        </option>
                    ))}
                </optgroup>
            )),
        [data.event]
    );

    return (
        <WorkflowBox
            category={translate('liste_des_missi_82050')}
            icon="sitemap"
            title={translate('une_mission_par_94044')}
        >
            <RichSelect
                isSearchVisible={true}
                isSelectAllVisible={true}
                label={translate('est_affect_u_18005')}
                multiple={true}
                name="workflow.data.positionsCategoriesIds"
                placeholder={translate('choisir_une_cat_82874')}
                state={props.isEdit ? 'disabled' : 'active'}
            >
                {categoriesOptions}
            </RichSelect>

            <RichSelect
                isSearchVisible={true}
                isSelectAllVisible={true}
                label={translate('est_affect_u_40348')}
                multiple={true}
                name="workflow.data.positionsIds"
                placeholder={translate('choisir_une_mis_90627')}
                state={props.isEdit ? 'disabled' : 'active'}
            >
                {positionsOptions}
            </RichSelect>

            <RichSelect
                isSearchVisible={true}
                isSelectAllVisible={true}
                label={translate('est_affect_u_82271')}
                multiple={true}
                name="workflow.data.positionsSlotsIds"
                placeholder={translate('choisir_un_cr_n_50945')}
                state={props.isEdit ? 'disabled' : 'active'}
            >
                {slotsOptions}
            </RichSelect>

            <RichSelect
                isSearchVisible={true}
                isSelectAllVisible={true}
                label={translate('est_affect_u_31860')}
                multiple={true}
                name="workflow.data.positionsSegmentsIds"
                placeholder={translate('choisir_une_vue_98781')}
                state={props.isEdit ? 'disabled' : 'active'}
            >
                {segmentsOptions}
            </RichSelect>
        </WorkflowBox>
    );
};
